import { COLUMN, FORMATTING, JARGON, LAYOUT } from "../../modules/constants"
import { addPaddingRightToLastCell, calcStats, getCell, getCellWithTooltip, headCell, setStats } from "../../modules/table.funcs"
import { shortMonthNameFromNumber } from "../../modules/utilities"
import { initStats2 } from "../../components/layout/InitStats"

const { birthYear, border1, float, integer, left, percent, right, string } = FORMATTING
const stats = {}
const borderLeft = border1
const nationalityWidth = "10%"
const residentofWidth = "10%"
const dobWidth = "5%"
const ageSexWidth = "5%"

const AUD = 0
const REM = 1
const NOM = 2
const CHAIR = 0
const MEMBER = 1

//#region Header
// Returns object with chair (c) and member (m) header cells
const getHeadCells = (committee, portrait, width) => {

    return {
        c: headCell({
            borderLeft: portrait ? null : borderLeft,
            id: committee.CHAIRS,
            width,
        }),
        m: headCell({
            id: committee.MEMBERS,
            width,
        }),
    }
}

export const createLsePeopleCommitteesHeadersData = props => {
    // console.log('createLsePeopleCommitteesHeadersData props:', props)
    const { portrait, the350club } = props
    const committeeWidth = portrait ? null : LAYOUT.COMMITTEE.WIDTH

    const aud = getHeadCells(COLUMN.COMMITTEE.AUDIT, portrait, committeeWidth)
    const rem = getHeadCells(COLUMN.COMMITTEE.REMUNERATION, portrait, committeeWidth)
    const nom = getHeadCells(COLUMN.COMMITTEE.NOMINATION, portrait, committeeWidth)

    const star = headCell({
        align: left,
        id: COLUMN.STAR,
        type: string,
        width: LAYOUT.STAR_WIDTH,
    })

    const officer = headCell({
        align: left,
        colSpan: portrait ? 2 : 1,
        id: COLUMN.OFFICER,
        label: "Name",
        paddingLeft: the350club ? LAYOUT.HOME.PADDING.BORDER : 0,
    })

    const roles = headCell({
        id: COLUMN.SKIP_ME,
        label: "Committee Roles",
        width: "5%",
        sortLabel: false,
    })

    const age = headCell({
        id: COLUMN.AGE,
        width: ageSexWidth
    })

    const dob = headCell({
        id: COLUMN.DOB,
        width: dobWidth
    })

    const nationality = headCell({
        align: left,
        borderLeft,
        id: COLUMN.NATIONALITY,
        paddingLeft: LAYOUT.HOME.PADDING.BORDER,
        width: nationalityWidth,
    })

    const peopleSex = headCell({
        id: COLUMN.SEX,
        width: ageSexWidth,
    })

    const residence = headCell({
        align: left,
        id: COLUMN.RESIDENCE,
        width: residentofWidth,
    })

    const headCells = portrait
        ? [star, officer, aud.c, rem.c, nom.c]
        : [star, officer, roles, aud.c, aud.m, rem.c, rem.m, nom.c, nom.m, nationality, residence, dob, age, peopleSex]

    if (the350club)
        headCells.shift() // Remove star

    addPaddingRightToLastCell(headCells)

    return headCells
}
//#endregion Header


//#region Footers
// Returns object with total and mean cells
const getFootCell = ({ borderLeft = null, column }) => ({
    total: getCell({ borderLeft, column, type: integer, value: stats[column].total }),
    mean: getCell({ borderLeft, column, precision: 2, value: stats[column].mean }),
})

// Returns object with chair (c) and member (m) header cells
const getFootCells = (committee, portrait) => {
    return {
        c: getFootCell({ borderLeft: portrait ? null : borderLeft, column: committee.CHAIRS }),
        m: getFootCell({ column: committee.MEMBERS }),
    }
}

export const createLsePeopleCommitteesFootersData = props => {
    // console.log('createLsePeopleFootersData props:',props)

    const { portrait, the350club } = props
    let column = null

    for (const property in stats)
        calcStats(stats[property])

    column = COLUMN.STAR
    const starFoot = {
        total: getCell({ align: left, column }),
        mean: getCell({ align: left, column })
    }

    const aud = getFootCells(COLUMN.COMMITTEE.AUDIT, portrait)
    const rem = getFootCells(COLUMN.COMMITTEE.REMUNERATION, portrait)
    const nom = getFootCells(COLUMN.COMMITTEE.NOMINATION, portrait)

    column = COLUMN.LEGEND
    const legend = {
        total: getCell({ column, label: "Total:" }),
        mean: getCell({ column, label: "Average:" }),
    }

    column = COLUMN.OFFICER
    const value = stats[column].total
    const nameFoot = {
        total: getCell({
            align: left,
            column,
            paddingLeft: the350club ? LAYOUT.HOME.PADDING.BORDER : 0,
            suffix: ` ${value === 1 ? JARGON.PERSON : JARGON.PEOPLE}`,
            type: integer,
            value,
        }),
        mean: getCell({ column })
    }

    column = COLUMN.NATIONALITY
    const nationality = {
        total: getCell({
            align: left,
            borderLeft,
            column,
            paddingLeft: LAYOUT.HOME.PADDING.BORDER,
        }),
        mean: getCell({
            align: left,
            borderLeft,
            column,
            paddingLeft: LAYOUT.HOME.PADDING.BORDER,
        }),
    }

    column = COLUMN.RESIDENCE
    const residence = {
        total: getCell({
            column,
            align: left,
        }),
        mean: getCell({
            column,
            align: left,
        }),
    }

    column = COLUMN.AGE
    const age = {
        total: getCell({
            column,
            type: integer,
            value: stats[column].total,
        }),
        mean: getCell({
            column,
            type: float,
            value: stats[column].mean,
        }),
    }

    column = COLUMN.DOB
    const dob = {
        total: getCell({
            column,
        }),
        mean: getCell({
            column,
            type: birthYear,
            value: stats[COLUMN.AGE].mean,
        }),
    }

    column = COLUMN.SEX
    const sex = {
        total: getCell({
            column,
            suffix: " F",
            type: integer,
            value: stats[column].total,
        }),
        mean: getCell({
            column,
            type: percent,
            value: stats[column].mean,
            suffix: "% F",
        }),
    }

    const footCells = {
        totals: [starFoot.total, nameFoot.total],
        means: [starFoot.mean, nameFoot.mean],
    }

    if (!portrait) {
        // Add the "Total" and "Average" legends
        footCells.totals.push(legend.total)
        footCells.means.push(legend.mean)

        footCells.totals.push(...[aud.c.total, aud.m.total, rem.c.total, rem.m.total, nom.c.total, nom.m.total])
        footCells.means.push(...[aud.c.mean, aud.m.mean, rem.c.mean, rem.m.mean, nom.c.mean, nom.m.mean])

        // Add other cells
        footCells.totals.push(nationality.total, residence.total, dob.total, age.total, sex.total)
        footCells.means.push(nationality.mean, residence.mean, dob.mean, age.mean, sex.mean)
    } else {
        // If portrait, only add the legend and age mean values.
        footCells.totals.push(legend.total, aud.c.total, rem.c.total, nom.c.total)
        footCells.means.push(legend.mean, aud.c.mean, rem.c.mean, nom.c.mean)
    }

    if (the350club) {
        footCells.totals.shift() // Remove starFoot
        footCells.means.shift()
    }

    // Add right padding to the rightmost cell
    addPaddingRightToLastCell(footCells.totals)
    addPaddingRightToLastCell(footCells.means)

    return footCells
}
//#endregion Footers

//#region Rows
export const createLsePeopleCommitteesRowData = props => {
    // console.log('createLsePeopleCommitteesRowData props:',props)
    const { items } = props
    // console.log('items:', items)
    const rows = []
    initStats2(stats)
    items.forEach(item => {
        rows.push(createLsePeopleRow({ ...props, item }))
    })

    return rows
}

// Sets chair and member stats and return object with chair (c) and member (m) cells
const rowStatsAndCells = ({ committee, cmteStats, portrait }) => {
    setStats(cmteStats[CHAIR], stats[committee.CHAIRS], true)
    setStats(cmteStats[MEMBER], stats[committee.MEMBERS], true)

    return {
        c: getCellWithTooltip({
            borderLeft: portrait ? null : borderLeft,
            value: cmteStats[CHAIR],
        }),
        m: getCellWithTooltip({
            value: cmteStats[MEMBER],
        }),
    }
}

const createLsePeopleRow = props => {
    // console.log('createLsePeopleRow props:',props)
    const { item, portrait, the350club } = props
    const { cmteStats, commonInfo, index, nameToSort, personInfo } = item

    let name = getCellWithTooltip({
        colSpan: 2, // Header and footers have a 'Legend' column (COLUMN.LEGEND)
        paddingLeft: the350club ? LAYOUT.HOME.PADDING.BORDER : 0,
        sortValue: nameToSort,
        type: string,
        value: commonInfo.name,
    })
    setStats(1, stats[COLUMN.OFFICER], true)

    const aud = rowStatsAndCells({ committee: COLUMN.COMMITTEE.AUDIT, cmteStats: cmteStats[AUD], portrait })
    const rem = rowStatsAndCells({ committee: COLUMN.COMMITTEE.REMUNERATION, cmteStats: cmteStats[REM], portrait })
    const nom = rowStatsAndCells({ committee: COLUMN.COMMITTEE.NOMINATION, cmteStats: cmteStats[NOM], portrait })

    const dob = personInfo.dateOfBirth
        ? `${shortMonthNameFromNumber(personInfo.dateOfBirth.month)} ${personInfo.dateOfBirth.year}`
        : JARGON.UNKNOWN

    const dobCell = getCellWithTooltip({
        align: right,
        type: string,
        value: dob,
    })

    const age = getCellWithTooltip({ value: personInfo.age })
    setStats(personInfo.age, stats[COLUMN.AGE], true)

    const sexCell = getCellWithTooltip(
        personInfo.sex === 1
            ? {
                align: right,
                tooltip: "female",
                type: string,
                value: "F",
            }
            : {
                align: right,
                tooltip: personInfo.sex === 0 ? "male" : "unknown",
                type: string,
                value: personInfo.sex === 0 ? "M" : JARGON.UNKNOWN
            }
    )

    if (personInfo.sex === 0 || personInfo.sex === 1) {
        stats[COLUMN.SEX].total += personInfo.sex
        stats[COLUMN.SEX].numof++
    }

    const nationality = getCellWithTooltip({
        align: left,
        borderLeft,
        paddingLeft: LAYOUT.HOME.PADDING.BORDER,
        type: string,
        value: personInfo.nationality,
    })

    const residence = getCellWithTooltip({
        align: left,
        type: string,
        value: personInfo.countryOfResidence,
    })

    const rowCells = portrait
        ? [name, aud.c, rem.c, nom.c]
        : [name, aud.c, aud.m, rem.c, rem.m, nom.c, nom.m, nationality, residence, dobCell, age, sexCell]

    addPaddingRightToLastCell(rowCells)

    return {
        index,
        link: commonInfo.officerIds ? `/person/${commonInfo.officerIds.join()}` : null,
        isFavourite: item.isFavourite,
        cellWithTooltips: rowCells,
    }
}
//#endregion Rows

